import React from "react";
import {
  FiCheck,
  FiEye,
  FiFileText,
  FiRefreshCw,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import {
  extractPrimaryPlaceName,
  formatDepartureDateTime,
  getApprovalSummary,
  formatMoney,
  getReceiptUrls,
  getStatusClass,
  getTollAmount,
  getTotalAmount,
  getTravelQuotationDisplayLabel,
  getTravelQuotationProjectLabel,
  getTravelAmount,
  truncate,
} from "./TravelExpenseApprovalUtils";
import TravelExpenseApprovalFlow from "./TravelExpenseApprovalFlow";
import { isAdmin } from "../../utils/buttonVisibility";

const TravelExpenseApprovalRow = ({
  item,
  index,
  checked,
  openingReceiptUrl,
  refreshingFlowId,
  onToggleSelect,
  onOpenReceiptModal,
  onApprove,
  onReject,
  onRefreshFlow,
  onDelete,
}) => {
  const admin = isAdmin();
  const receiptUrls = getReceiptUrls(item);
  const originLabel = extractPrimaryPlaceName(item.origin);
  const destinationLabel = extractPrimaryPlaceName(item.destination);
  const quotationLabel = getTravelQuotationDisplayLabel(item);
  const projectLabel = getTravelQuotationProjectLabel(item);

  return (
    <tr
      className={`border-t border-gray-100 dark:border-gray-700 transition-colors duration-200 ${
        index % 2 === 0
          ? "bg-white dark:bg-gray-900"
          : "bg-gray-50 dark:bg-gray-800"
      } hover:bg-blue-50 dark:hover:bg-blue-900/20`}
    >
      <td className="px-4 py-3 text-center">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggleSelect(item._id)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          aria-label={`Select ${item.requestedBy || item._id}`}
        />
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-200">
        {formatDepartureDateTime(item.departureDateTime)}
      </td>
      <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
        {item.requestedBy || "-"}
      </td>
      <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
        <div className="font-medium break-words">{truncate(quotationLabel, 48)}</div>
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {truncate(projectLabel, 48)}
        </div>
      </td>
      <td
        className="px-4 py-3 max-w-[260px] text-gray-700 dark:text-gray-200"
        title={originLabel}
      >
        {truncate(originLabel || "-")}
      </td>
      <td
        className="px-4 py-3 max-w-[260px] text-gray-700 dark:text-gray-200"
        title={destinationLabel}
      >
        {truncate(destinationLabel || "-")}
      </td>
      <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">
        <div className="whitespace-nowrap font-semibold">
          {formatMoney(getTotalAmount(item))} บาท
        </div>
        <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          เดินทาง {formatMoney(getTravelAmount(item))} + ทางด่วน{" "}
          {formatMoney(getTollAmount(item))}
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        {receiptUrls.length > 0 ? (
          <button
            type="button"
            onClick={() => onOpenReceiptModal(item)}
            disabled={openingReceiptUrl === item._id}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-800/60 transition-colors disabled:bg-gray-100 disabled:text-gray-400"
            title="ดูหลักฐานทั้งหมด"
          >
            <FiEye size={15} />
            <span className="text-xs font-medium">
              {openingReceiptUrl === item._id
                ? "Loading..."
                : `${receiptUrls.length} ไฟล์`}
            </span>
          </button>
        ) : (
          <span className="inline-flex items-center justify-center p-2 rounded-md bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">
            <FiFileText size={16} />
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        <TravelExpenseApprovalFlow item={item} />
      </td>
      <td className="px-4 py-3 text-center">
        <span
          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(
            item.status
          )}`}
        >
          {item.status}
        </span>
        <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
          {getApprovalSummary(item)}
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-2">
          {item.status === "Pending" && item.canApprove ? (
            <>
              <button
                type="button"
                onClick={() => onApprove(item._id)}
                className="inline-flex items-center justify-center p-2 rounded-md bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300 dark:hover:bg-green-800/60 transition-colors"
                title="Approve"
              >
                <FiCheck size={16} />
              </button>
              <button
                type="button"
                onClick={() => onReject(item._id)}
                className="inline-flex items-center justify-center p-2 rounded-md bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-800/60 transition-colors"
                title="Reject"
              >
                <FiX size={16} />
              </button>
            </>
          ) : null}

          {admin ? (
            <>
              {["Pending", "Rejected"].includes(item.status) ? (
                <button
                  type="button"
                  onClick={() => onRefreshFlow(item)}
                  disabled={refreshingFlowId === item._id}
                  className="inline-flex items-center justify-center p-2 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-800/60 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  title="Refresh approval flow"
                >
                  <FiRefreshCw
                    size={16}
                    className={refreshingFlowId === item._id ? "animate-spin" : ""}
                  />
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => onDelete(item)}
                className="inline-flex items-center justify-center p-2 rounded-md bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-800/60 transition-colors"
                title="Delete"
              >
                <FiTrash2 size={16} />
              </button>
            </>
          ) : item.status === "Pending" ? null : (
            "-"
          )}
        </div>
      </td>
    </tr>
  );
};

export default TravelExpenseApprovalRow;
